import * as THREE from 'three';
import type { World } from '../sim/world';
import type { BatteryAim } from '../sim/cannons';
import { MAX_HALF_BEAM } from '../data/ship';
import { AIM_PARTS, type AimPart } from '../sim/gunnery';
import type { HitMarkers } from './hitMarkers';

const PART_NAMES: Record<AimPart, string> = { waterline: 'Waterline', hull: 'Hull', rigging: 'Rigging' };

// The words that go with the aim (render/aim.ts draws the ring on the sea):
// a small caption under the ring - range, time of flight, and whatever is
// keeping the guns off the mark - and the controls, under the broadside rows
// of the gun panel, while a broadside is being laid. Laid on a ship (R), she
// is bracketed, with her name.

const el = (tag: string, cls: string): HTMLElement => {
  const e = document.createElement(tag);
  e.className = cls;
  return e;
};

export class AimHud {
  private readonly caption: HTMLElement;
  private readonly status: HTMLElement;
  private readonly detail: HTMLElement;
  private readonly lock: HTMLElement;
  private readonly lockName: HTMLElement;
  private readonly lockTally: HTMLElement;
  private readonly p = new THREE.Vector3();
  private readonly q = new THREE.Vector3();
  private barHtml = '';
  /** The player picks where on a ship to lay (a click on the gun panel's choice). */
  onPart: (part: AimPart) => void = () => {};

  /** `hint`: where the controls go (under the gun panel's broadside rows); `hits`: our hits on each ship, for the bracket. */
  constructor(wrap: HTMLElement, private readonly hint: HTMLElement, private readonly hits?: HitMarkers) {
    this.caption = el('div', 'aim-caption hidden');
    this.status = el('div', 'aim-status');
    this.detail = el('div', 'aim-detail');
    this.caption.append(this.status, this.detail);
    this.lock = el('div', 'aim-lock hidden');
    for (const c of ['tl', 'tr', 'bl', 'br']) this.lock.appendChild(el('i', c));
    this.lockName = el('div', 'aim-lock-name');
    this.lockTally = el('div', 'aim-lock-tally');
    this.lock.append(this.lockName, this.lockTally);
    wrap.append(this.lock, this.caption);
    hint.addEventListener('click', (e) => {
      const part = (e.target as HTMLElement).closest<HTMLElement>('[data-part]')?.dataset.part as AimPart | undefined;
      if (part && AIM_PARTS.includes(part)) this.onPart(part);
    });
  }

  /** `freeCam`: Space, R and F fly the free camera, so only a click fires there. `part`: where on a ship the guns are laid. */
  update(world: World, camera: THREE.PerspectiveCamera, freeCam: boolean, part: AimPart): void {
    const b = world.player.battery;
    const aim = b.aim;
    this.caption.classList.toggle('hidden', !aim);
    this.hint.classList.toggle('hidden', !aim);
    this.bracket(world, camera);
    if (!aim) return;

    const locked = !!world.aimTarget;
    const follow = freeCam
      ? `<span><kbd>Click</kbd> a ship: follow her</span>`
      : `<span>${locked ? '<kbd>R</kbd> let her go' : '<kbd>R</kbd> or click a ship: follow her'}</span>`;
    const bar = `<div class="aim-parts">${freeCam ? '' : '<span class="gun-key">F</span>'}<span class="stat-label">Lay on</span>`
      + `<div class="ammo-seg">${AIM_PARTS.map((p) => `<span data-part="${p}" class="${p === part ? 'on' : ''}">${PART_NAMES[p]}</span>`).join('')}</div></div>`
      + `<div class="aim-keys"><span><kbd>Click</kbd>${freeCam ? '' : '<kbd>Space</kbd>'} fire</span>`
      + `${follow}<span><kbd>Shift</kbd> fine</span><span><kbd>Esc</kbd> stand down</span></div>`;
    if (bar !== this.barHtml) this.hint.innerHTML = this.barHtml = bar;

    // Under the ring: its centre and its nearest edge, as the camera sees them.
    const c = this.p.set(aim.x, 0, aim.z);
    const toCam = this.q.set(camera.position.x - aim.x, 0, camera.position.z - aim.z);
    const dist = toCam.length() || 1;
    const edge = toCam.multiplyScalar(Math.min(aim.spread, dist * 0.5) / dist).add(c);
    c.project(camera);
    edge.project(camera);
    if (c.z > 1 || edge.z > 1) {
      this.caption.classList.add('hidden');
      return;
    }
    const w = window.innerWidth, h = window.innerHeight;
    const x = (c.x * 0.5 + 0.5) * w;
    const y = Math.max((-c.y * 0.5 + 0.5) * h, (-edge.y * 0.5 + 0.5) * h) + 18;
    this.caption.style.transform = `translate(${Math.round(x)}px, ${Math.round(Math.min(y, h - 120))}px) translateX(-50%)`;

    const [text, tone] = this.words(world, aim);
    this.status.textContent = text;
    this.status.className = `aim-status ${tone}`;
    const loaded = aim.loaded < aim.guns ? ` · ${aim.loaded} of ${aim.guns} loaded` : '';
    const t = world.aimTarget;
    const lead = t ? Math.hypot(aim.x - t.x, aim.z - t.z) : 0;
    const leading = lead >= 2 ? ` · leading ${Math.round(lead)} m` : '';
    const high = part !== 'waterline' ? ` · ${part}` : '';
    this.detail.textContent = `${Math.round(aim.range)} m · ${aim.tof.toFixed(1)} s${high}${leading}${loaded}`;
  }

  /** Brackets about the ship the guns are laid on, if they're laid on one, and her name over them. */
  private bracket(world: World, camera: THREE.PerspectiveCamera): void {
    const t = world.aimTarget;
    const v = t && world.player.battery.aim ? world.vessels.find((u) => u.id === t.id) : undefined;
    if (!v) {
      this.lock.classList.add('hidden');
      return;
    }
    // Her hull and lower rigging: the corners of a box about her, on screen.
    const s = v.ship;
    const hl = 19.5 * s.sx, hb = MAX_HALF_BEAM * s.sz, top = 14 * s.sy;
    const h = s.heading, fx = Math.sin(h), fz = -Math.cos(h);
    const w = window.innerWidth, H = window.innerHeight;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const a of [-hl, hl]) for (const b of [-hb, hb]) for (const y of [0, top]) {
      const p = this.p.set(s.com.x + fx * a - fz * b, y, s.com.z + fz * a + fx * b).project(camera);
      if (p.z > 1) {
        this.lock.classList.add('hidden');
        return;
      }
      const sx = (p.x * 0.5 + 0.5) * w, sy = (-p.y * 0.5 + 0.5) * H;
      x0 = Math.min(x0, sx);
      x1 = Math.max(x1, sx);
      y0 = Math.min(y0, sy);
      y1 = Math.max(y1, sy);
    }
    // Never smaller than a small mark, however far off she is.
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    const bw = Math.max(x1 - x0, 22) + 12, bh = Math.max(y1 - y0, 16) + 12;
    this.lock.classList.remove('hidden');
    this.lock.style.transform = `translate(${Math.round(cx - bw / 2)}px, ${Math.round(cy - bh / 2)}px)`;
    this.lock.style.width = `${Math.round(bw)}px`;
    this.lock.style.height = `${Math.round(bh)}px`;
    if (this.lockName.textContent !== v.name) this.lockName.textContent = v.name;
    // Our shot striking her: the corners flash, and the burst is counted.
    const since = this.hits?.since(v.id) ?? Infinity;
    this.lock.classList.toggle('hit', since < 0.3);
    const n = this.hits?.tally(v.id) ?? 0;
    const tally = n > 0 ? `${n} hit${n > 1 ? 's' : ''}` : '';
    if (this.lockTally.textContent !== tally) this.lockTally.textContent = tally;
  }

  /** What's keeping the guns off the mark, if anything, and how it's shown (good, busy, bad). */
  private words(world: World, aim: BatteryAim): [string, string] {
    switch (aim.status) {
      case 'arc': return ['Beyond the guns’ traverse', 'bad'];
      case 'range': return ['Out of range', 'bad'];
      case 'close': return ['Too close to depress the guns', 'bad'];
      case 'heel': return ['She heels too far for the guns to bear', 'bad'];
    }
    const b = world.player.battery;
    const guns = b.guns.filter((g) => g.spec.side === aim.side && !g.destroyed);
    if (!guns.length) return ['No guns left on this side', 'bad'];
    if (aim.loaded === 0) return [`Reloading ${Math.round(b.reloadProgress(aim.side) * 100)}%`, 'busy'];
    if (!guns.some((g) => g.crew > 0.2)) return ['Crews to their guns…', 'busy'];
    if (aim.laid < aim.bearing) return [`Laying the guns — ${aim.laid} of ${aim.bearing} on`, 'busy'];
    if (aim.bearing < aim.guns) return [`${aim.bearing} of ${aim.guns} guns bear`, 'busy'];
    const t = world.aimTarget;
    const name = t ? world.vessels.find((v) => v.id === t.id)?.name : undefined;
    return [name ? `Laid on the ${name}` : 'Laid', 'good'];
  }
}
